// A diagnosis is one string. Picked from ICD-10-GM it reads "J06.9 G Akute Infektion der oberen
// Atemwege" — code, certainty marker, optional side, title; free text stays as typed. Same rules as
// the backend's utils/diagnosisText.ts (the stats key diagnoses by code from this format).

/** ICD-10-GM Zusatzkennzeichen für die Diagnosesicherheit: gesichert, Verdacht, ausgeschlossen, Zustand nach. */
export const CERTAINTIES = ["G", "V", "A", "Z"] as const;
export type Certainty = (typeof CERTAINTIES)[number];
/** Seitenlokalisation: rechts, links, beidseitig. */
export const SIDES = ["R", "L", "B"] as const;
export type Side = (typeof SIDES)[number];

export type ParsedDiagnosis = { code: string | null; certainty: Certainty | null; side: Side | null; title: string };

const PATTERN = /^([A-Z]\d{2}(?:\.\d{1,2})?)(?=\s|$)(?:\s+([GVAZ])(?=\s|$))?(?:\s+([RLB])(?=\s|$))?\s*(.*)$/;

export function parseDiagnosis(text: string): ParsedDiagnosis {
    const clean = text.trim().replace(/\s+/g, " ");
    const m = PATTERN.exec(clean);
    if (!m) return { code: null, certainty: null, side: null, title: clean };
    return { code: m[1], certainty: (m[2] as Certainty) ?? null, side: (m[3] as Side) ?? null, title: m[4] };
}

// ICD-10-GM has no laterality flag, so R/L/B is offered up front only where a side is common: eye and ear,
// musculoskeletal, injuries, and a few paired organs/limbs. Elsewhere it stays one click away ("+ Seite").
const SIDE_RANGES: [string, string][] = [
    ["H00", "H95"], // eye, ear
    ["M00", "M99"], // musculoskeletal
    ["S00", "S99"], // injuries
    ["T00", "T14"], // injuries, several/unspecified regions
    ["C50", "C50"], ["D05", "D05"], ["N60", "N64"], // breast
    ["N13", "N13"], ["N20", "N20"], // kidney, ureter
    ["I80", "I80"], ["I83", "I83"], // leg veins
    ["G56", "G57"], // nerves of arm and leg
];

/** Is a side (R/L/B) usual for this ICD code? Compared on the 3-character category. */
export const sideIsCommon = (code: string) => {
    const category = code.slice(0, 3);
    return SIDE_RANGES.some(([from, to]) => category >= from && category <= to);
};

export const formatDiagnosis =(d: ParsedDiagnosis) => [d.code, d.code && d.certainty, d.code && d.side, d.title].filter(Boolean).join(" ");

/** A coded diagnosis without a certainty marker gets G (confirmed) — the usual case. A code typed in
 *  lower case ("i10.90 …") counts as a code. */
export function withDefaultCertainty(text: string): string {
    const clean = text.trim().replace(/\s+/g, " ").replace(/^[a-z]\d{2}(?:\.\d{1,2})?(?=\s|$)/, (code) => code.toUpperCase());
    const d = parseDiagnosis(clean);
    return d.code && !d.certainty ? formatDiagnosis({ ...d, certainty: "G" }) : clean;
}
